/**
 * TemplateFilterModal Component
 * 
 * Modal for selecting filters when using a template to generate a report.
 * Provides patient selection, date range, and template-specific filters.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import { 
    BsCalendar, 
    BsPerson, 
    BsFilter, 
    BsDownload, 
    BsX,
    BsSearch,
    BsCheck,
    BsListCheck,
    BsEye,
    BsEyeSlash,
    BsGrid3X3Gap
} from 'react-icons/bs';
import { toast } from 'react-hot-toast';
import reportingService from '../../services/reportingService';
import PropTypes from 'prop-types';

const TemplateFilterModal = ({ 
    show, 
    onHide, 
    template, 
    onGenerateReport 
}) => {
    // State for filters
    const [filters, setFilters] = useState({
        patientId: '',
        patientName: '',
        startDate: '',
        endDate: '',
        customFilters: {}
    });
    
    // State for patient selection
    const [patients, setPatients] = useState([]);
    const [patientSearch, setPatientSearch] = useState('');
    const [patientsLoading, setPatientsLoading] = useState(false);
    const [showAllPatients, setShowAllPatients] = useState(true);
    
    // State for template filters
    const [templateFilters, setTemplateFilters] = useState([]);
    const [filtersLoading, setFiltersLoading] = useState(false);
    
    // State for generation
    const [generating, setGenerating] = useState(false);
    
    // State for export format
    const [exportFormat, setExportFormat] = useState('pdf');
    
    // State for field selection
    const [availableFields, setAvailableFields] = useState([]);
    const [selectedFields, setSelectedFields] = useState(new Set());
    const [fieldCategories, setFieldCategories] = useState({});
    const [fieldSearch, setFieldSearch] = useState('');
    const [showFieldSelection, setShowFieldSelection] = useState(true);

    // Load patients on modal open
    useEffect(() => {
        if (show && template) {
            loadInitialData();
        }
    }, [show, template]);

    // Load initial data
    const loadInitialData = async () => {
        await Promise.all([
            loadPatients(),
            loadTemplateFilters(),
            loadTemplateFields()
        ]);
    };

    // Load patients list
    const loadPatients = async () => {
        try {
            setPatientsLoading(true);
            const response = await reportingService.getPatients({ 
                limit: 50
            });
            
            if (response.success) {
                // Handle both direct array and nested object response formats
                const patientsData = response.data?.patients || response.data || [];
                setPatients(patientsData);
            } else {
                toast.error('Failed to load patients');
            }
        } catch (error) {
            toast.error('Failed to load patients');
        } finally {
            setPatientsLoading(false);
        }
    };

    // Load template-specific filters
    const loadTemplateFilters = async () => {
        if (!template?.available_filters) return;
        
        try {
            setFiltersLoading(true);
            
            // Create filter definitions from template available_filters
            const filterDefs = template.available_filters.map(filterName => ({
                id: filterName,
                name: filterName,
                label: formatFilterLabel(filterName),
                type: determineFilterType(filterName),
                required: false
            }));
            
            setTemplateFilters(filterDefs);
            
            // Initialize custom filters state
            const initialCustomFilters = {};
            filterDefs.forEach(filter => {
                initialCustomFilters[filter.id] = '';
            });
            
            setFilters(prev => ({
                ...prev,
                customFilters: initialCustomFilters
            }));
            
        } catch (error) {
            toast.error('Failed to load template filters');
        } finally {
            setFiltersLoading(false);
        }
    };

    // Load template fields for selection
    const loadTemplateFields = async () => {
        if (!template?.field_definitions) return;
        
        try {
            // Extract fields from template field definitions
            const fields = template.field_definitions.map(fieldDef => ({
                id: fieldDef.field_name,
                name: fieldDef.field_name,
                label: fieldDef.field_label || formatFieldLabel(fieldDef.field_name),
                type: fieldDef.field_type || 'string',
                category: fieldDef.category || categorizeField(fieldDef.field_name),
                required: fieldDef.is_required || false,
                description: fieldDef.description || ''
            }));
            
            // Categorize fields
            const categories = categorizeFields(fields);
            
            setAvailableFields(fields);
            setFieldCategories(categories);
            
            // Set default selected fields (required + common fields)
            const defaultSelected = new Set();
            fields.forEach(field => {
                if (field.required || isCommonField(field.name)) {
                    defaultSelected.add(field.id);
                }
            });
            setSelectedFields(defaultSelected);
            
        } catch (error) {
            toast.error('Failed to load template fields');
        }
    };

    // Format filter label from field name
    const formatFilterLabel = (fieldName) => {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase())
            .replace(/^Form Data\./, ''); // Remove form_data prefix if present
    };

    // Determine filter type based on field name
    const determineFilterType = (fieldName) => {
        const lowerField = fieldName.toLowerCase();
        
        if (lowerField.includes('date') || lowerField.includes('time')) {
            return 'date';
        } else if (lowerField.includes('count') || lowerField.includes('score') || lowerField.includes('number')) {
            return 'number';
        } else if (lowerField.includes('status') || lowerField.includes('type') || lowerField.includes('category')) {
            return 'select';
        }
        
        return 'text';
    };

    // Format field label from field name
    const formatFieldLabel = (fieldName) => {
        return fieldName
            .replace(/^form_data\./, '') // Remove form_data prefix
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    // Categorize individual field
    const categorizeField = (fieldName) => {
        const lowerField = fieldName.toLowerCase();
        
        // Patient demographics
        if (lowerField.includes('patient') || lowerField.includes('name') || 
            lowerField.includes('age') || lowerField.includes('gender') ||
            lowerField.includes('dob') || lowerField.includes('birth') ||
            lowerField.includes('contact') || lowerField.includes('phone') ||
            lowerField.includes('email') || lowerField.includes('address')) {
            return 'patient_demographics';
        }
        
        // Medical information
        if (lowerField.includes('diagnosis') || lowerField.includes('condition') ||
            lowerField.includes('medication') || lowerField.includes('vital') ||
            lowerField.includes('blood') || lowerField.includes('pressure') ||
            lowerField.includes('temperature') || lowerField.includes('weight') ||
            lowerField.includes('height') || lowerField.includes('medical')) {
            return 'medical_information';
        }
        
        // Administrative data
        if (lowerField.includes('id') || lowerField.includes('mrn') ||
            lowerField.includes('created') || lowerField.includes('updated') ||
            lowerField.includes('submitted') || lowerField.includes('user') ||
            lowerField.includes('staff') || lowerField.includes('facility')) {
            return 'administrative';
        }
        
        // Default to form data
        return 'form_data';
    };

    // Categorize all fields into groups
    const categorizeFields = (fields) => {
        const categories = {
            patient_demographics: {
                label: 'Patient Demographics',
                icon: 'BsPerson',
                fields: []
            },
            medical_information: {
                label: 'Medical Information',
                icon: 'BsHeartPulse',
                fields: []
            },
            form_data: {
                label: 'Form Data',
                icon: 'BsGrid3X3Gap',
                fields: []
            },
            administrative: {
                label: 'Administrative',
                icon: 'BsFileText',
                fields: []
            }
        };
        
        fields.forEach(field => {
            const category = field.category;
            if (categories[category]) {
                categories[category].fields.push(field);
            } else {
                categories.form_data.fields.push(field);
            }
        });
        
        return categories;
    };

    // Check if field is commonly selected by default
    const isCommonField = (fieldName) => {
        const commonFields = ['patient_name', 'patient_id', 'submitted_at', 'created_at'];
        return commonFields.some(common => fieldName.includes(common));
    };

    // Handle patient search
    const handlePatientSearch = async (searchTerm) => {
        setPatientSearch(searchTerm);
        
        if (searchTerm.length < 2) {
            await loadPatients(); // Load all patients if search is too short
            return;
        }
        
        try {
            setPatientsLoading(true);
            const response = await reportingService.searchPatients(searchTerm, 20);
            
            if (response.success) {
                // Handle both direct array and nested object response formats
                const patientsData = response.data?.patients || response.data || [];
                setPatients(patientsData);
            }
        } catch (error) {
        } finally {
            setPatientsLoading(false);
        }
    };

    // Handle patient selection
    const handlePatientSelect = (patient) => {
        if (patient) {
            const patientName = patient.full_name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
            setFilters(prev => ({
                ...prev,
                patientId: patient.id,
                patientName: patientName
            }));
            setShowAllPatients(false);
        } else {
            // "All Patients" selected
            setFilters(prev => ({
                ...prev,
                patientId: '',
                patientName: ''
            }));
            setShowAllPatients(true);
        }
    };

    // Handle custom filter change
    const handleCustomFilterChange = (filterId, value) => {
        setFilters(prev => ({
            ...prev,
            customFilters: {
                ...prev.customFilters,
                [filterId]: value
            }
        }));
    };

    // Handle field selection toggle
    const handleFieldToggle = (fieldId) => {
        setSelectedFields(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(fieldId)) {
                // Check if it's a required field
                const field = availableFields.find(f => f.id === fieldId);
                if (field && field.required) {
                    toast.error(`${field.label} is required and cannot be deselected`);
                    return prev;
                }
                newSelected.delete(fieldId);
            } else {
                newSelected.add(fieldId);
            }
            return newSelected;
        });
    };

    // Handle select all fields in category
    const handleCategoryToggle = (categoryKey, selectAll) => {
        setSelectedFields(prev => {
            const newSelected = new Set(prev);
            const categoryFields = fieldCategories[categoryKey]?.fields || [];
            
            categoryFields.forEach(field => {
                if (selectAll) {
                    newSelected.add(field.id);
                } else if (!field.required) {
                    newSelected.delete(field.id);
                }
            });
            
            return newSelected;
        });
    };

    // Handle select all fields
    const handleSelectAllFields = (selectAll) => {
        setSelectedFields(prev => {
            if (selectAll) {
                return new Set(availableFields.map(f => f.id));
            } else {
                // Only keep required fields
                return new Set(availableFields.filter(f => f.required).map(f => f.id));
            }
        });
    };

    // Filter fields based on search
    const getFilteredFields = (fields) => {
        if (!fieldSearch) return fields;
        const searchLower = fieldSearch.toLowerCase();
        return fields.filter(field => 
            field.label.toLowerCase().includes(searchLower) ||
            field.name.toLowerCase().includes(searchLower)
        );
    };

    // Handle form submission
    const handleGenerateReport = async (e) => {
        e.preventDefault();
        
        if (!filters.startDate) {
            toast.error('Please select a start date');
            return;
        }
        
        if (selectedFields.size === 0) {
            toast.error('Please select at least one field for the report');
            return;
        }
        
        try {
            setGenerating(true);
            
            // Build filter conditions
            const filterConditions = [];
            
            // Add patient filter if specific patient selected
            if (filters.patientId) {
                filterConditions.push({
                    field: 'patient_id',
                    operator: 'eq',
                    value: filters.patientId,
                    data_type: 'string'
                });
            }
            
            // Add custom filters with proper data type handling
            Object.entries(filters.customFilters).forEach(([fieldName, value]) => {
                if (value && String(value).trim()) {
                    const filterDef = templateFilters.find(f => f.id === fieldName);
                    let processedValue = value;
                    let dataType = 'string';
                    let operator = 'contains';
                    
                    // Determine proper data type and operator based on filter definition
                    if (filterDef?.type === 'number') {
                        processedValue = parseFloat(value);
                        dataType = 'number';
                        operator = 'gte';
                    } else if (filterDef?.type === 'date') {
                        processedValue = value; // Keep as string in ISO format
                        dataType = 'date';
                        operator = 'gte';
                    } else {
                        processedValue = String(value).trim();
                        dataType = 'string';
                        operator = 'contains';
                    }
                    
                    filterConditions.push({
                        field: fieldName,
                        operator: operator,
                        value: processedValue,
                        data_type: dataType
                    });
                }
            });
            
            // Build report generation request matching backend schema
            const reportRequest = {
                template_id: template.id,
                filters: {
                    conditions: filterConditions,
                    logic_operator: 'and',
                    date_range: {
                        start: filters.startDate + 'T00:00:00Z', // Convert to ISO datetime
                        end: (filters.endDate || new Date().toISOString().split('T')[0]) + 'T23:59:59Z'
                    }
                },
                export_format: exportFormat,
                selected_fields: Array.from(selectedFields), // Include selected fields
                parameters: {
                    name: `${template.name} - ${showAllPatients ? 'All Patients' : filters.patientName}`,
                    description: `Report generated from ${template.name} template${!showAllPatients ? ` for patient ${filters.patientName}` : ''}`,
                    auto_generated: false,
                    patient_filter: showAllPatients ? 'all' : 'specific',
                    field_count: selectedFields.size
                }
            };
            
            // Debug: Log the request being sent
            
            // Call the onGenerateReport callback
            await onGenerateReport(reportRequest);
            
            // Close modal on success
            onHide();
            
        } catch (error) {
            toast.error('Failed to generate report');
        } finally {
            setGenerating(false);
        }
    };

    // Reset form when modal closes
    const handleModalHide = () => {
        setFilters({
            patientId: '',
            patientName: '',
            startDate: '',
            endDate: '',
            customFilters: {}
        });
        setPatientSearch('');
        setShowAllPatients(true);
        setSelectedFields(new Set());
        setFieldSearch('');
        setAvailableFields([]);
        setFieldCategories({});
        onHide();
    };

    if (!template) return null;

    return (
        <Modal show={show} onHide={handleModalHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <BsFilter className="me-2" />
                    Configure Report Filters
                </Modal.Title>
            </Modal.Header>
            
            <Form onSubmit={handleGenerateReport}>
                <Modal.Body>
                    {/* Template Info */}
                    <Alert variant="info" className="mb-4">
                        <div className="d-flex align-items-center">
                            <Badge bg="primary" className="me-2">{template.category}</Badge>
                            <strong>{template.name}</strong>
                        </div>
                        <small className="text-muted d-block mt-1">
                            {template.description || 'Generate a report using this template with your selected filters.'}
                        </small>
                    </Alert>

                    <Row>
                        {/* Patient Selection */}
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <BsPerson className="me-1" />
                                    Patient Selection
                                </Form.Label>
                                
                                <div className="mb-2">
                                    <Form.Check
                                        type="radio"
                                        name="patientSelection"
                                        id="allPatients"
                                        label="All Patients"
                                        checked={showAllPatients}
                                        onChange={() => handlePatientSelect(null)}
                                    />
                                </div>
                                
                                <div className="mb-2">
                                    <Form.Check
                                        type="radio"
                                        name="patientSelection"
                                        id="specificPatient"
                                        label="Specific Patient"
                                        checked={!showAllPatients}
                                        onChange={() => setShowAllPatients(false)}
                                    />
                                </div>
                                
                                {!showAllPatients && (
                                    <div className="mt-2">
                                        <div className="position-relative mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Search patients..."
                                                value={patientSearch}
                                                onChange={(e) => handlePatientSearch(e.target.value)}
                                                className="pe-5"
                                            />
                                            <BsSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                                        </div>
                                        
                                        {patientsLoading ? (
                                            <div className="text-center py-2">
                                                <Spinner animation="border" size="sm" />
                                            </div>
                                        ) : (
                                            <Form.Select
                                                value={filters.patientId}
                                                onChange={(e) => {
                                                    const selectedPatient = patients.find(p => 
                                                        p.id === e.target.value
                                                    );
                                                    handlePatientSelect(selectedPatient);
                                                }}
                                                disabled={showAllPatients}
                                            >
                                                <option value="">Select a patient...</option>
                                                {patients.map(patient => (
                                                    <option 
                                                        key={patient.id} 
                                                        value={patient.id}
                                                    >
                                                        {patient.full_name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim()} 
                                                        ({patient.current_mrn || patient.mrn || patient.id})
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        )}
                                        
                                        {filters.patientName && (
                                            <div className="mt-2">
                                                <Badge bg="success">
                                                    <BsCheck className="me-1" />
                                                    {filters.patientName}
                                                </Badge>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        {/* Date Range */}
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <BsCalendar className="me-1" />
                                    Date Range *
                                </Form.Label>
                                <Row>
                                    <Col>
                                        <Form.Control
                                            type="date"
                                            placeholder="Start Date"
                                            value={filters.startDate}
                                            onChange={(e) => setFilters(prev => ({
                                                ...prev,
                                                startDate: e.target.value
                                            }))}
                                            required
                                        />
                                        <Form.Text className="text-muted">Start Date</Form.Text>
                                    </Col>
                                    <Col>
                                        <Form.Control
                                            type="date"
                                            placeholder="End Date"
                                            value={filters.endDate}
                                            onChange={(e) => setFilters(prev => ({
                                                ...prev,
                                                endDate: e.target.value
                                            }))}
                                        />
                                        <Form.Text className="text-muted">End Date (optional)</Form.Text>
                                    </Col>
                                </Row>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Template-Specific Filters */}
                    {templateFilters.length > 0 && (
                        <div>
                            <h6 className="mb-3">Additional Filters</h6>
                            <Row>
                                {templateFilters.map(filter => (
                                    <Col md={6} key={filter.id} className="mb-3">
                                        <Form.Group>
                                            <Form.Label>
                                                {filter.label}
                                                {filter.required && <span className="text-danger"> *</span>}
                                            </Form.Label>
                                            <Form.Control
                                                type={filter.type === 'number' ? 'number' : 
                                                      filter.type === 'date' ? 'date' : 'text'}
                                                placeholder={`Filter by ${filter.label.toLowerCase()}`}
                                                value={filters.customFilters[filter.id] || ''}
                                                onChange={(e) => handleCustomFilterChange(filter.id, e.target.value)}
                                                required={filter.required}
                                            />
                                            <Form.Text className="text-muted">
                                                Optional filter for {filter.label.toLowerCase()}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {filtersLoading && (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading template filters...
                        </div>
                    )}

                    {/* Field Selection Section */}
                    {availableFields.length > 0 && (
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">
                                    <BsListCheck className="me-2" />
                                    Report Fields
                                    <Badge bg="secondary" className="ms-2">{selectedFields.size} selected</Badge>
                                </h6>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={() => handleSelectAllFields(true)}
                                    >
                                        Select All
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => handleSelectAllFields(false)}
                                    >
                                        Clear
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => setShowFieldSelection(!showFieldSelection)}
                                    >
                                        {showFieldSelection ? <BsEyeSlash /> : <BsEye />}
                                    </Button>
                                </div>
                            </div>

                            {showFieldSelection && (
                                <>
                                    {/* Field Search */}
                                    <div className="mb-3">
                                        <div className="position-relative">
                                            <Form.Control
                                                type="text"
                                                placeholder="Search fields..."
                                                value={fieldSearch}
                                                onChange={(e) => setFieldSearch(e.target.value)}
                                                className="pe-5"
                                                size="sm"
                                            />
                                            <BsSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                                        </div>
                                    </div>

                                    {/* Field Categories */}
                                    {Object.entries(fieldCategories).map(([categoryKey, category]) => {
                                        const filteredFields = getFilteredFields(category.fields);
                                        const selectedInCategory = filteredFields.filter(f => selectedFields.has(f.id)).length;
                                        
                                        if (filteredFields.length === 0) return null;
                                        
                                        return (
                                            <div key={categoryKey} className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="mb-0 text-muted">
                                                        <BsGrid3X3Gap className="me-2" />
                                                        {category.label}
                                                        <Badge bg="light" text="dark" className="ms-2">
                                                            {selectedInCategory}/{filteredFields.length}
                                                        </Badge>
                                                    </h6>
                                                    <div className="btn-group" role="group">
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            onClick={() => handleCategoryToggle(categoryKey, true)}
                                                        >
                                                            All
                                                        </Button>
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            size="sm"
                                                            onClick={() => handleCategoryToggle(categoryKey, false)}
                                                        >
                                                            None
                                                        </Button>
                                                    </div>
                                                </div>
                                                
                                                <Row>
                                                    {filteredFields.map(field => (
                                                        <Col md={6} lg={4} key={field.id} className="mb-2">
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`field-${field.id}`}
                                                                checked={selectedFields.has(field.id)}
                                                                onChange={() => handleFieldToggle(field.id)}
                                                                label={
                                                                    <div className="d-flex align-items-center">
                                                                        <span className={field.required ? 'fw-bold' : ''}>
                                                                            {field.label}
                                                                        </span>
                                                                        {field.required && (
                                                                            <Badge bg="warning" size="sm" className="ms-1">
                                                                                Required
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                }
                                                                disabled={field.required && selectedFields.has(field.id)}
                                                            />
                                                            {field.description && (
                                                                <Form.Text className="text-muted small">
                                                                    {field.description}
                                                                </Form.Text>
                                                            )}
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </div>
                                        );
                                    })}

                                    {Object.values(fieldCategories).every(cat => getFilteredFields(cat.fields).length === 0) && (
                                        <div className="text-center py-3 text-muted">
                                            <BsSearch className="mb-2" size={24} />
                                            <div>No fields found matching "{fieldSearch}"</div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    
                    {/* Export Format Selection */}
                    <div className="mb-3">
                        <Form.Label>
                            <BsDownload className="me-2" />
                            Export Format
                        </Form.Label>
                        <Form.Select 
                            value={exportFormat} 
                            onChange={(e) => setExportFormat(e.target.value)}
                            className="form-select"
                        >
                            <option value="pdf">PDF</option>
                            <option value="excel">Excel</option>
                            <option value="csv">CSV</option>
                            <option value="json">JSON</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Choose the format for your generated report
                        </Form.Text>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalHide} disabled={generating}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={generating || !filters.startDate}
                    >
                        {generating ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <BsDownload className="me-2" />
                                Generate Report
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

TemplateFilterModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    template: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        category: PropTypes.string,
        available_filters: PropTypes.arrayOf(PropTypes.string)
    }),
    onGenerateReport: PropTypes.func.isRequired
};

export default TemplateFilterModal;